import React, {useState} from "react"

import {ChatbotEvent} from "@proto/chatbot/v1alpha/chatbot"
import {useTheme} from "@/hooks/useTheme"
import {useWebsocket} from "@/hooks/useWebsocket"

interface IProps {
    message: string
}

interface ReportThresholdsInput {
    max_article_count: number
    max_article_hits: number
    max_distance: number
    message: string
}

const ReportThresholdsMessage = ({message}: IProps) => {
    const {send, addMessage} = useWebsocket()
    const {BotIcon} = useTheme()
    const [submitted, setSubmitted] = useState(false)

    // Parse the incoming message
    let thresholdData: ReportThresholdsInput
    try {
        thresholdData = JSON.parse(message)
    } catch (e) {
        console.error("Failed to parse threshold data:", e)
        return null
    }

    // Form state
    const [maxArticleCount, setMaxArticleCount] = useState(thresholdData.max_article_count)
    const [maxArticleHits, setMaxArticleHits] = useState(thresholdData.max_article_hits)
    const [maxDistance, setMaxDistance] = useState(thresholdData.max_distance)

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (submitted) return

        // Validate inputs
        if (maxArticleCount <= 0 || maxArticleCount > 100000) {
            alert("Max article count must be between 1 and 100,000")
            return
        }
        if (maxArticleHits <= 0) {
            alert("Max article hits must be greater than 0")
            return
        }
        if (maxDistance <= 0 || maxDistance > 1) {
            alert("Max distance must be between 0 and 1")
            return
        }

        const thresholdResponse = {
            max_article_count: maxArticleCount,
            max_article_hits: maxArticleHits,
            max_distance: maxDistance,
        }

        // Send the threshold configuration to the backend
        const um: ChatbotEvent = {
            type: "user",
            message: JSON.stringify(thresholdResponse),
            id: "",
            clientId: "",
        }
        send(JSON.stringify(um))

        // Add a display message for the user
        const displayMessage = `Selected thresholds: ${maxArticleCount} articles, ${maxArticleHits.toLocaleString()} hits, ${maxDistance} distance`
        const dm: ChatbotEvent = {
            type: "user",
            message: displayMessage,
            id: "",
            clientId: "",
        }
        addMessage(dm)

        setSubmitted(true)
    }

    return (
        <div className="m-[20px] ml-0 mr-0 flex items-start justify-start">
            <div className="mr-[10px]">{BotIcon}</div>
            <div className="min-w-[400px] rounded-2xl rounded-tl-none bg-response p-[20px] pb-[12px] pt-[12px]">
                <p className="mb-4 whitespace-pre-wrap">{thresholdData.message}</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="maxArticleCount" className="mb-1 block text-sm font-medium">
                            Max Article Count (1 - 100,000)
                        </label>
                        <input
                            type="number"
                            id="maxArticleCount"
                            value={maxArticleCount}
                            onChange={(e) => setMaxArticleCount(parseInt(e.target.value) || 0)}
                            min="1"
                            max="100000"
                            disabled={submitted}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                    </div>

                    <div>
                        <label htmlFor="maxArticleHits" className="mb-1 block text-sm font-medium">
                            Max Article Hits
                        </label>
                        <input
                            type="number"
                            id="maxArticleHits"
                            value={maxArticleHits}
                            onChange={(e) => setMaxArticleHits(parseInt(e.target.value) || 0)}
                            min="1"
                            disabled={submitted}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                    </div>

                    <div>
                        <label htmlFor="maxDistance" className="mb-1 block text-sm font-medium">
                            Max Similarity Distance (0.0 - 1.0)
                        </label>
                        <input
                            type="number"
                            id="maxDistance"
                            value={maxDistance}
                            onChange={(e) => setMaxDistance(parseFloat(e.target.value) || 0)}
                            min="0"
                            max="1"
                            step="0.01"
                            disabled={submitted}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={submitted}
                        className="w-full rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-400"
                    >
                        {submitted ? "Submitted" : "Set Thresholds"}
                    </button>
                </form>
            </div>
        </div>
    )
}

export default ReportThresholdsMessage
